import React, { useEffect } from 'react';
import Alert from '@amzn/awsui-components-core/alert';
import Button from '@amzn/awsui-components-core/button';
import Modal from '@amzn/awsui-components-core/modal';
import { StarGridLoading } from 'src/components/star-grid/components/StarGridLoading';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import { STUDIO_DATASET_PREFIX, useStudioSelection, type StudioSource } from 'src/components/star-grid/hooks/useStudioSelection';
import { isTemplateDatasetMissing } from 'src/components/star-grid/utils/studioDataSource';
import { useReportStudio } from 'src/components/star-grid/hooks/useReportStudio';
import { useStudioReportSpec } from 'src/components/star-grid/hooks/useStudioReportSpec';
import { useStarGridText } from 'src/components/star-grid/hooks/useStarGridText';
import type { StudioTemplate } from 'src/components/star-grid/reportStudio.types';
import { StudioCompactSelect } from 'src/components/star-grid/components/StudioCompactSelect';
import { StudioTemplateChooser } from 'src/components/star-grid/components/StudioTemplateChooser';
import type { StudioImportableReport } from 'src/components/star-grid/hooks/useStudioTemplateChooser';
import { StudioPalette } from 'src/components/star-grid/components/StudioPalette';
import { StudioHeader } from 'src/components/star-grid/components/StudioHeader';
import { StudioShelves } from 'src/components/star-grid/components/StudioShelves';
import { StudioPreview } from 'src/components/star-grid/components/StudioPreview';
import { StudioLineSets } from 'src/components/star-grid/components/StudioLineSets';
import styles from 'src/components/star-grid/components/styles/ReportStudio.module.scss';
import headerStyles from 'src/components/star-grid/components/styles/StudioHeader.module.scss';
import { useStudioHeaderNavigation } from 'src/components/star-grid/hooks/useStudioHeaderNavigation';
import { useDataSourceSelection } from 'src/components/star-grid/datasets/hooks/useDataSourceSelection';
import { DataSourcePicker } from 'src/components/star-grid/datasets/components/DataSourcePicker';
import { DatasetWizard } from 'src/components/star-grid/datasets/components/DatasetWizard';
interface Props {
  readonly onCreated: (id: number, name?: string) => void;
  /** Omitted, the Studio starts from a template. Supplied, it reopens one of these saved reports. */
  readonly source?: StudioSource;
  /** Saved reports the CREATE page offers as a starting point to copy from. */
  readonly importable?: readonly StudioImportableReport[];
}
interface EditorProps extends Props {
  readonly template: StudioTemplate;
  readonly onDirty: (dirty: boolean) => void;
  readonly chooser: React.ReactNode;
  readonly shelvesRef: React.RefObject<HTMLDivElement>;
  /** The saved report being edited, so its spec loads instead of a blank one. */
  readonly editingReportId?: string;
  /** The data source picker, rendered in the details row for the P&L (built-in) flow. */
  readonly dataSourceControl?: React.ReactNode;
}
const StudioEditor: React.FC<EditorProps> = ({ template, onCreated, onDirty, chooser, shelvesRef, editingReportId, dataSourceControl }) => {
  const state = useReportStudio(template, onDirty);
  const { report, templateLabel, startFromTemplate, importScreenSetup, importReportSetup, saveReport } = useStudioReportSpec(
    state,
    editingReportId,
    onCreated
  );
  return (
    <>
      {/* No tab switcher: editing a row's members goes straight to the line set and straight back. */}
      {state.tab === 'lines' ? (
        <>
          <div className={styles.lineSetBar}>
            <Button iconName="angle-left" onClick={() => state.setTab('studio')} data-testid="studio-back-to-report">
              {state.text('studioBackToReport')}
            </Button>
            <h2>{state.text('studioLineSet')}</h2>
          </div>
          <StudioLineSets state={state} />
        </>
      ) : (
        <>
          <StudioHeader
            state={state}
            chooser={chooser}
            onStartTemplate={startFromTemplate}
            onImportScreen={importScreenSetup}
            onImportReport={importReportSetup}
            templateLabel={templateLabel}
            dataSourceControl={dataSourceControl}
          />
          <div className={styles.workspace}>
            <StudioPalette state={state} />
            <div className={styles.shelves} ref={shelvesRef}>
              <StudioShelves state={state} />
              <StudioPreview
                state={state}
                previewGrid={report.grid}
                previewRunning={report.running}
                previewError={report.error}
                onSave={saveReport}
                saving={report.saving}
                // The name is checked on the field and rows/columns on the shelves; the spec only supplies the data source.
                canSave={Boolean(report.spec?.cube_source || report.spec?.dataset)}
              />
            </div>
          </div>
        </>
      )}
    </>
  );
};
/** A picked dataset opens the same Studio on that dataset; picking P&L again returns to its templates. */
function useDatasetSelection(datasetId: string | null, state: ReturnType<typeof useStudioSelection>): void {
  const { choose, catalog, selectedId } = state;
  useEffect(() => {
    // A datasource published after the catalog loaded is not in it yet; read it again so the editor can open it.
    if (datasetId && catalog.data && !catalog.data.datasets.some((item) => item.id === datasetId)) catalog.reload();
    if (datasetId) choose(`${STUDIO_DATASET_PREFIX}${datasetId}`);
    else if (selectedId.startsWith(STUDIO_DATASET_PREFIX)) {
      const first = catalog.data?.templates.find((item) => item.ready) ?? catalog.data?.templates[0];
      if (first) choose(first.id);
    }
    // Follows the dataset pick only: `choose` is a fresh closure each render.
  }, [datasetId]);
}

export const ReportStudioView: React.FC<Props> = ({ onCreated, source, importable }) => {
  const text = useStarGridText();
  const dataSource = useDataSourceSelection();
  const state = useStudioSelection(source, text('studioCopySuffix'));
  const navigation = useStudioHeaderNavigation();
  useDatasetSelection(dataSource.selectedDatasetId, state);
  // A saved or published datasource changes what the catalog offers for it, so re-read it -- but only over a
  // clean draft: the reload remounts the editor, which would drop unsaved shelf work.
  const { reload: reloadCatalog } = state.catalog;
  const { dirty } = state;
  useEffect(() => {
    if (dataSource.savedRevision > 0 && !dirty) reloadCatalog();
    // Follows the saves only: a draft turning clean later must not trigger a reload by itself.
  }, [dataSource.savedRevision]);
  // The switch is dirty-guarded: picking a data source only defers the switch until the user confirms.
  // On cancel the editor stays put, so snap the picker back to the data source it is actually showing.
  const cancelSwitch = () => {
    state.cancel();
    if (state.selectedId.startsWith(STUDIO_DATASET_PREFIX)) dataSource.selectDataset(state.selectedId.slice(STUDIO_DATASET_PREFIX.length));
    else dataSource.selectPnl();
  };
  const datasetUnavailable =
    dataSource.selectedDatasetId && state.catalog.data && !state.catalog.data.datasets.some((item) => item.id === dataSource.selectedDatasetId);
  const templateDatasetMissing = isTemplateDatasetMissing(state.template.data);
  const picker = (
    <div className={headerStyles.dataSourceCell}>
      <DataSourcePicker
        text={text}
        datasets={dataSource.datasets}
        value={dataSource.selectedId}
        onSelectPnl={dataSource.selectPnl}
        onSelectDataset={dataSource.selectDataset}
        loading={dataSource.datasetsLoading}
        loadError={dataSource.datasetsError}
      />
    </div>
  );
  const wizard = dataSource.wizardOpen ? (
    <DatasetWizard
      text={text}
      visible
      editingId={dataSource.wizardEditingId ?? undefined}
      onDismiss={dataSource.closeWizard}
      onSaved={dataSource.onWizardSaved}
    />
  ) : null;
  // Nothing renders in stages: while the catalog or a template is loading the page is its background
  // and one centred spinner. Showing the title and the template picker first meant the screen arrived
  // in three visible steps, each one re-laying out what came before it.
  if (state.catalog.loading || state.template.loading) {
    return (
      <div className={styles.page} data-testid="report-studio">
        <StarGridLoading label={text(state.editingReport ? 'studioLoadingReport' : 'studioLoadingTemplate')} />
      </div>
    );
  }
  const chooser =
    state.catalog.data &&
    (state.editingReport && source?.kind === 'report' ? (
      // The help reads as a tooltip: a line under this one field put Applicable Decks and Deck Layout
      // out of line with it.
      <label className={headerStyles.chooser} title={text('studioChooseReportHelp')}>
        <span className={headerStyles.fieldLabel}>{text('studioChooseReport')}</span>
        <StudioCompactSelect
          label={text('studioChooseReport')}
          value={state.selectedId}
          options={source.reports.map((report) => ({ id: String(report.id), label: report.label }))}
          onChange={state.choose}
          testId="studio-template"
          placeholder={text('studioChooseReportPlaceholder')}
        />
      </label>
    ) : (
      <StudioTemplateChooser
        templates={state.catalog.data.templates}
        selectedId={state.selectedId}
        onChange={state.choose}
        text={text}
        importable={importable}
      />
    ));
  return (
    <div className={styles.page} data-testid="report-studio">
      <header className={headerStyles.pageHeading}>
        <div>
          <h1>{text(state.editingReport ? 'studioHeaderEditTitle' : 'createReportTitle')}</h1>
          <p>{text('studioHeaderHelp')}</p>
        </div>
      </header>
      {state.catalog.error && (
        <Alert type="error" action={<Button onClick={state.catalog.reload}>{text('retry')}</Button>}>
          {state.catalog.error.message}
        </Alert>
      )}
      {datasetUnavailable && <Alert type="info">{text('studioDatasetNoBindings')}</Alert>}
      {(!state.template.data || (templateDatasetMissing && state.editingReport)) && <div className={headerStyles.fallbackChooser}>{chooser}</div>}
      {state.template.error && (
        <Alert type="error" action={<Button onClick={state.template.reload}>{text('retry')}</Button>}>
          {state.template.error.message}
        </Alert>
      )}
      {templateDatasetMissing && !datasetUnavailable && <Alert type="error">{text('studioDatasetMissing')}</Alert>}
      {/* Without an editor its header is gone, so the data source picker stays here to move back to P&L. */}
      {templateDatasetMissing && !state.editingReport && <div className={headerStyles.fallbackChooser}>{picker}</div>}
      {state.template.data && !state.template.loading && !templateDatasetMissing && (
        <StudioEditor
          key={state.selectedId}
          template={state.template.data}
          onCreated={onCreated}
          onDirty={state.setDirty}
          chooser={chooser}
          shelvesRef={navigation.shelvesRef}
          editingReportId={state.editingReport && source?.kind === 'report' ? state.selectedId : undefined}
          dataSourceControl={state.editingReport ? undefined : picker}
        />
      )}
      {wizard}
      {!state.catalog.loading && !state.editingReport && state.catalog.data?.templates.length === 0 && <Alert>{text('studioNoTemplates')}</Alert>}
      {state.editingReport && source?.kind === 'report' && source.reports.length === 0 && <Alert>{text('studioNoEditableReports')}</Alert>}
      <Modal
        visible={Boolean(state.pendingId)}
        header={text('studioSwitchTemplate')}
        closeAriaLabel={text('studioClose')}
        onDismiss={cancelSwitch}
        footer={
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={cancelSwitch}>{text('deckRestoreCancel')}</Button>
            <Button variant="primary" onClick={state.confirm}>
              {text('studioDiscard')}
            </Button>
          </SpaceBetween>
        }
      >
        {text('studioUnsaved')}
      </Modal>
    </div>
  );
};
